import React, { useState, useEffect } from "react";
import { FiEdit3, FiEye, FiEyeOff, FiX, FiClock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { userApiService } from "../../../service/admin/userApiService";
import { useMessage, ToastTypes } from "../../../base/context/MessageProvider";
import { useLoader } from "../../../base/context/LoaderProvider";

const CreditModal = ({ user, onUpdated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [password, setPassword] = useState("");
  const [note, setNote] = useState("");
  const [operationType, setOperationType] = useState("add"); // 'add' or 'subtract'
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentCreditAmount, setCurrentCreditAmount] = useState(0);

  const message = useMessage();
  const loader = useLoader();
  const navigate = useNavigate();

  const handleViewCreditPage = () => {
    if (!user?.email) {
      message.showToast("User email not found", ToastTypes.ERROR);
      return;
    }
    // Navigate to credit management page with email filter
    navigate("/admin/credit-management", {
      state: { email: user.email },
    });
  };

  const handleViewTransactionHistory = () => {
    if (!user?.email) {
      message.showToast("User email not found", ToastTypes.ERROR);
      return;
    }
    // Navigate to transactions page with email filter
    navigate("/admin/transactions", {
      state: { email: user.email },
    });
  };

  const fetchCreditData = async () => {
    if (!user?._id) return;
    try {
      setLoadingHistory(true);
      const res = await userApiService.getCreditByUserId(user._id);
      if (res.status.code === 200 && res.data) {
        setCurrentCreditAmount(res.data.created_amount || res.data.credit_amount || 0);
        // Sort history by date descending (newest first)
        const sortedHistory = [...(res.data.created_history || res.data.credit_history || [])].sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA; // Newest first
        });
        setHistory(sortedHistory);
      }
    } catch (err) {
      console.error("Error fetching credit data:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openModal = () => {
    setAmount("");
    setPassword("");
    setNote("");
    setOperationType("add");
    setShowPassword(false);
    setIsOpen(true);
    fetchCreditData();
  };

  const closeModal = () => {
    setIsOpen(false);
    setIsSubmitting(false);
  };

  const handleAddCredit = async () => {
    if (isSubmitting) return;
    
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      message.showToast("Please enter valid amount to add", ToastTypes.ERROR);
      return;
    }
    
    if (!password.trim()) {
      message.showToast("Password is required", ToastTypes.ERROR);
      return;
    }

    try {
      setIsSubmitting(true);
      loader.showLoading(true);
      
      const res = await userApiService.setCreditAmount(
        user._id,
        numAmount,
        password,
        note
      );
      
      loader.showLoading(false);

      if (res.status.code === 200) {
        message.showToast("Credit amount added successfully", ToastTypes.SUCCESS);
        closeModal();
        onUpdated && onUpdated();
      } else {
        message.showToast(
          res?.status?.message || res?.error?.message || "Failed to add credit amount",
          ToastTypes.ERROR
        );
      }
    } catch (err) {
      loader.showLoading(false);
      setIsSubmitting(false);
      message.showToast(
        err?.message || "Server error while adding credit amount",
        ToastTypes.ERROR
      );
    } finally {
      loader.showLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleSubtractCredit = async () => {
    if (isSubmitting) return;
    
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      message.showToast("Please enter valid amount to subtract", ToastTypes.ERROR);
      return;
    }
    
    if (!password.trim()) {
      message.showToast("Password is required", ToastTypes.ERROR);
      return;
    }

    // Check if resulting credit amount would be negative
    if (currentCreditAmount - numAmount < 0) {
      message.showToast("Credit amount cannot be negative", ToastTypes.ERROR);
      return;
    }

    try {
      setIsSubmitting(true);
      loader.showLoading(true);
      
      const res = await userApiService.setCreditAmount(
        user._id,
        -numAmount, // Negative amount for subtraction
        password,
        note
      );
      
      loader.showLoading(false);

      if (res.status.code === 200) {
        message.showToast("Credit amount subtracted successfully", ToastTypes.SUCCESS);
        closeModal();
        onUpdated && onUpdated();
      } else {
        message.showToast(
          res?.status?.message || res?.error?.message || "Failed to subtract credit amount",
          ToastTypes.ERROR
        );
      }
    } catch (err) {
      loader.showLoading(false);
      setIsSubmitting(false);
      message.showToast(
        err?.message || "Server error while subtracting credit amount",
        ToastTypes.ERROR
      );
    } finally {
      loader.showLoading(false);
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs sm:text-sm text-purple-600 hover:text-purple-800"
        onClick={openModal}
        title="Set Credit Amount"
      >
        <FiEdit3 className="inline-block" />
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={closeModal}>
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3 sm:px-6 flex-shrink-0">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-slate-900">
                  Set Credit Amount
                </h2>
                <p className="text-xs sm:text-sm text-slate-500">
                  {user.firstName} {user.lastName} ({user.email})
                </p>
              </div>
              <button
                type="button"
                className="p-1 rounded-full hover:bg-slate-100"
                onClick={closeModal}
              >
                <FiX />
              </button>
            </div>

            {/* Body - Two Column Layout */}
            <div className="flex flex-1 overflow-hidden">
              {/* Left Side - History */}
              <div className="w-1/2 border-r border-slate-200 flex flex-col">
                <div className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiClock className="text-slate-500" size={16} />
                      <h3 className="text-sm font-semibold text-slate-700">
                        Credit Amount History
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="text-xs text-purple-600 hover:underline"
                        onClick={handleViewCreditPage}
                      >
                        View Credit Amount Page
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        type="button"
                        className="text-xs text-purple-600 hover:underline"
                        onClick={handleViewTransactionHistory}
                      >
                        View Transaction History
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3 wallet-history-scroll">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center h-full text-slate-500 text-sm">
                      Loading history...
                    </div>
                  ) : history.length > 0 ? (
                    <div className="space-y-3">
                      {history.map((item, idx) => (
                        <div
                          key={idx}
                          className="bg-slate-50 rounded-lg p-3 text-xs border border-slate-200"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="font-medium text-slate-700 mb-1">
                                {item.adminEmail || "Admin"}
                              </div>
                              {item.adminNote && (
                                <div className="text-slate-600 mt-1 text-xs">
                                  {item.adminNote}
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-2">
                              <div
                                className={`font-semibold text-sm ${
                                  (typeof item.amount === 'string' && item.amount.startsWith("+")) || 
                                  (typeof item.amount === 'number' && item.amount > 0)
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {(typeof item.amount === 'string' && item.amount.startsWith("+")) || 
                                 (typeof item.amount === 'number' && item.amount > 0) ? "+" : ""}₹
                                {Math.abs(typeof item.amount === 'string' ? parseFloat(item.amount.replace(/[+-]/g, '')) : item.amount).toLocaleString("en-IN")}
                              </div>
                              <div className="text-slate-500 text-xs mt-1">
                                {formatDate(item.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className="text-slate-500 text-xs mt-1 pt-1 border-t border-slate-200">
                            Previous: ₹{Number(item.previousAmount || 0).toLocaleString("en-IN")} → 
                            New: ₹{Number(item.newAmount || 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                      No history found
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="w-1/2 flex flex-col">
                <div className="px-4 py-3 sm:px-6 sm:py-5 space-y-4 flex-1 overflow-y-auto">
                  {/* Current Credit Amount */}
                  <div className="flex items-center justify-between text-sm pb-2 border-b border-slate-200">
                    <span className="text-slate-500">Current Credit Amount</span>
                    <span className="font-semibold text-purple-600 text-lg">
                      ₹{Number(currentCreditAmount).toLocaleString("en-IN")}
                    </span>
                  </div>

                  {/* Current Wallet Balance */}
                  <div className="flex items-center justify-between text-sm pb-2 border-b border-slate-200">
                    <span className="text-slate-500">Current Wallet Balance</span>
                    <span className="font-semibold text-slate-900 text-lg">
                      ₹{Number(user.walletBalance ?? 0).toLocaleString("en-IN")}
                    </span>
                  </div>

                  {/* Operation Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Operation Type
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setOperationType("add")}
                        className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                          operationType === "add"
                            ? "bg-green-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        Add Credit
                      </button>
                      <button
                        type="button"
                        onClick={() => setOperationType("subtract")}
                        className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                          operationType === "subtract"
                            ? "bg-red-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        Subtract Credit
                      </button>
                    </div>
                  </div>

                  {/* Amount Input */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Amount (₹)
                    </label>
                    <input
                      type="number"
                      className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={`Enter amount to ${operationType === "add" ? "add" : "subtract"}`}
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {operationType === "add" 
                        ? "Enter amount to add to credit" 
                        : "Enter amount to subtract from credit"}
                    </p>
                  </div>

                  {/* Note Field */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Note / Message
                    </label>
                    <textarea
                      rows="3"
                      className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Enter note or reason for this change"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Admin Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="block w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter Admin Password"
                        name="adminCreatedAmountPassword"
                        autoComplete="new-password"
                        inputMode="text"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700"
                        onClick={() => setShowPassword((p) => !p)}
                      >
                        {showPassword ? (
                          <FiEyeOff size={16} />
                        ) : (
                          <FiEye size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex flex-col-reverse gap-2 border-t px-4 py-3 sm:flex-row sm:justify-end sm:px-6 flex-shrink-0">
                  <button
                    type="button"
                    className="w-full sm:w-auto rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 bg-white hover:bg-slate-100"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  {operationType === "add" ? (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={handleAddCredit}
                    >
                      {isSubmitting ? "Processing..." : "Add Credit"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={handleSubtractCredit}
                    >
                      {isSubmitting ? "Processing..." : "Subtract Credit"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreditModal;

